import { Box, Skeleton, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { DetailSheet } from '../../../components/DetailSheet'
import { FormModal } from '../../../components/FormModal'
import { formatDateTime, formatPhone } from '../../../helpers'
import { VideoQuizComplete } from '../../../services/VideoQuiz/types'
import { VideoQuizService } from '../../../services/VideoQuiz/VideoQuiz.service'

/** Larguras das barras de resposta do esqueleto — irregulares de propósito, fixas por posição. */
const SKELETON_ANSWER_WIDTHS = ['85%', '60%', '72%', '45%']

interface IProps {
  isOpen: boolean
  onClose: () => void
  id?: number
}

export function VideoQuizViewModal({ isOpen, onClose, id }: IProps) {
  const service = new VideoQuizService()
  const [loading, setLoading] = useState(true)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [videoQuiz, setVideoQuiz] = useState<VideoQuizComplete>({} as any)

  useEffect(() => {
    if (!id) return

    setLoading(true)
    service
      .get(id)
      .then((response) => {
        setVideoQuiz(response)
      })
      .finally(() => setLoading(false))
  }, [id])

  const calcPercentage = (value1: number, value2: number): string => {
    if (value1 === 0 && value2 === 0) return '0.00'

    return ((value1 / (value1 + value2)) * 100).toFixed(2)
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Skeleton isLoaded={!loading} width="50%">
          {videoQuiz.name || '-'}
        </Skeleton>
      }
      size="wide"
    >
      <DetailSheet
        isLoading={loading}
        fields={[
          { label: 'Nome do Aluno', value: videoQuiz.name },
          { label: 'E-mail', value: videoQuiz.email },
          { label: 'Telefone', value: formatPhone(videoQuiz.phone) },
          { label: 'Vídeo Atual', value: videoQuiz.current_step },
          {
            label: 'Acertos',
            value: (
              <>
                {videoQuiz.correct_answers_count}{' '}
                <small>({calcPercentage(videoQuiz.correct_answers_count, videoQuiz.incorrect_answers_count)}%)</small>
              </>
            )
          },
          {
            label: 'Erros',
            value: (
              <>
                {videoQuiz.incorrect_answers_count}{' '}
                <small>({calcPercentage(videoQuiz.incorrect_answers_count, videoQuiz.correct_answers_count)}%)</small>
              </>
            )
          },
          { label: 'Iniciado em', value: videoQuiz.created_at && formatDateTime(videoQuiz.created_at) }
        ]}
      />

      <Text my={4} fontWeight="bold">
        Respostas enviadas
      </Text>

      {loading &&
        Array.from({ length: 6 }).map((_, index) => (
          <Box key={index} shadow="sm" p={3} my={4} borderRadius="md">
            <Skeleton width="100%" height="20px" mb={2} />
            {/* Largura fixa por posição: com `Math.random()` cada render sorteava outra, e as
                barras ficavam mudando de tamanho enquanto a requisição não voltava. */}
            {SKELETON_ANSWER_WIDTHS.map((width, index) => (
              <Skeleton key={index} width={width} height="20px" mb={1} />
            ))}
            <Skeleton width="40%" height="20px" mb={1} mt={2} ml="auto" />
          </Box>
        ))}

      {!loading &&
        videoQuiz.questions.map((question, index) => (
          <Box key={index} shadow="sm" p={3} my={4} borderRadius="md">
            <Text mb={2}>{question.question}</Text>
            {question.answers.map((answer, index) => (
              <Text key={index} color={answer.correct ? 'green.500' : answer.user_answer ? 'red.500' : 'black'}>
                <strong>{String.fromCharCode(65 + index)}</strong> - {answer.answer}
              </Text>
            ))}
            <Text mt={2} fontSize="xs" textAlign="right">
              Resposta realizada em: {formatDateTime(question.created_at)}
            </Text>
          </Box>
        ))}
    </FormModal>
  )
}
